/**
 * [TD-89 v2 Verdict 7.A] Generic confirmation modal primitive.
 *
 * Props:
 *   open: bool
 *   onConfirm: () => void | Promise<void>
 *   onCancel: () => void
 *   title: string
 *   body: ReactNode
 *   confirmLabel?: string             (default "Confirm")
 *   destructive?: bool                (red CTA + warning styling)
 *   typedConfirmation?: string        (if set, user must type this phrase to enable confirm)
 *   isDark: bool
 *   testIdPrefix?: string             (default "confirm-modal")
 */
import { useEffect, useRef, useState } from "react";
import { X, AlertTriangle } from "lucide-react";

export default function ConfirmModal({
  open,
  onConfirm,
  onCancel,
  title,
  body,
  confirmLabel = "Confirm",
  destructive = false,
  typedConfirmation,
  isDark,
  testIdPrefix = "confirm-modal",
}) {
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTyped("");
      setSubmitting(false);
      if (typedConfirmation) setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, typedConfirmation]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
      if (e.key === "Enter" && canConfirm && !submitting) handleConfirm();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, typed, submitting]);

  if (!open) return null;

  const canConfirm = !typedConfirmation || typed === typedConfirmation;

  const handleConfirm = async () => {
    if (!canConfirm || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm?.();
    } finally {
      setSubmitting(false);
    }
  };

  const cardBg = isDark ? "bg-[#1C1C1C] border-[#252525]" : "bg-white border-black/[0.10]";
  const text = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const muted = isDark ? "text-[rgba(255,255,255,0.55)]" : "text-[rgba(0,0,0,0.55)]";
  const inputCls = isDark
    ? "bg-[#111111] border-[#252525] text-[#F5F5F5] placeholder-[#A0A0A0]/40"
    : "bg-[#F0F0F0] border-black/[0.10] text-[#0A0A0A] placeholder-black/30";
  const ghostBtn = isDark ? "bg-white/[0.06] hover:bg-white/[0.12] text-[#F5F5F5]" : "bg-black/[0.06] hover:bg-black/[0.12] text-[#0A0A0A]";
  const confirmBtn = destructive
    ? "bg-[#E03030] hover:bg-[#F04040] text-white"
    : "bg-[#FF6B00] hover:bg-[#FF8533] text-white";

  return (
    <div
      data-testid={`${testIdPrefix}-root`}
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className={`relative w-full max-w-md rounded-2xl border p-6 shadow-2xl ${cardBg}`}>
        <button
          onClick={onCancel}
          className={`absolute top-3 right-3 ${muted} hover:text-[#FF6B00] transition-colors`}
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-2 mb-2">
          {destructive && <AlertTriangle size={18} className="text-[#E03030]" />}
          <h2 data-testid={`${testIdPrefix}-title`} className={`font-display font-bold text-lg uppercase ${text}`}>
            {title}
          </h2>
        </div>
        <div data-testid={`${testIdPrefix}-body`} className={`font-body text-sm mb-5 ${muted}`}>
          {body}
        </div>
        {typedConfirmation && (
          <div className="mb-4">
            <p className={`font-body text-xs mb-2 ${muted}`}>
              Type <span className={`font-mono font-bold ${text}`}>{typedConfirmation}</span> to confirm:
            </p>
            <input
              ref={inputRef}
              data-testid={`${testIdPrefix}-typed-input`}
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              spellCheck={false}
              autoComplete="off"
              autoCapitalize="off"
              className={`w-full rounded-lg border px-3 py-2 font-mono text-sm outline-none focus:border-[#FF6B00] transition-colors ${inputCls}`}
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          <button
            data-testid={`${testIdPrefix}-cancel-btn`}
            onClick={onCancel}
            className={`flex-1 py-2.5 rounded-lg font-display font-bold text-sm uppercase tracking-wider transition-all ${ghostBtn}`}
          >
            Cancel
          </button>
          <button
            data-testid={`${testIdPrefix}-confirm-btn`}
            disabled={!canConfirm || submitting}
            onClick={handleConfirm}
            className={`flex-1 py-2.5 rounded-lg font-display font-black uppercase tracking-wider text-sm transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${confirmBtn}`}
          >
            {submitting ? "Working..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
